import { cloneWorkflowGraph } from './cloneWorkflowGraph.js';
import { expandWorkflowFolderStepIds } from './expandWorkflowFolderStepIds.js';
import { getWorkflowGraphSelection } from './getWorkflowGraphSelection.js';
import { getParentGraphStep } from './getParentGraphStep.js';
import { randomId } from '../utils/randomId.js';

// Helper function to build a graph from steps (for copied steps)
const buildGraphFromSteps = (steps) => {
  const graph = {};
  steps.forEach((step, index) => {
    const nextStep = steps[index + 1];
    graph[step.id] = {
      exit: nextStep ? nextStep.id : null,
      child: null,
      branches: []
    };
  });
  return graph;
};

const findDisconnectedSubgraphs = (stepIds, graph) => {
  const stepIdSet = new Set(stepIds);
  const hasIncomingConnection = new Set();
  const visited = new Set();
  const subgraphs = [];

  stepIds.forEach((stepId) => {
    const node = graph[stepId];
    if (node) {
      if (node.exit && stepIdSet.has(node.exit)) {
        hasIncomingConnection.add(node.exit);
      }
      if (node.child && stepIdSet.has(node.child)) {
        hasIncomingConnection.add(node.child);
      }
      if (node.branches) {
        node.branches.forEach((branchId) => {
          if (branchId && stepIdSet.has(branchId)) {
            hasIncomingConnection.add(branchId);
          }
        });
      }
    }
  });

  const roots = stepIds.filter((stepId) => !hasIncomingConnection.has(stepId));

  roots.forEach((rootId) => {
    if (!visited.has(rootId)) {
      const subgraph = [];
      const traverse = (stepId) => {
        if (visited.has(stepId) || !stepIdSet.has(stepId)) return;
        visited.add(stepId);
        subgraph.push(stepId);

        const node = graph[stepId];
        if (node) {
          if (node.exit) traverse(node.exit);
          if (node.child) traverse(node.child);
          if (node.branches) {
            node.branches.forEach((branchId) => {
              if (branchId) traverse(branchId);
            });
          }
        }
      };

      traverse(rootId);
      if (subgraph.length > 0) {
        subgraphs.push({ root: rootId, steps: subgraph });
      }
    }
  });

  return subgraphs;
};

const getLastStepInSubgraph = (subgraph, graph) => {
  for (const stepId of subgraph.steps) {
    const node = graph[stepId];
    if (!node?.exit || !subgraph.steps.includes(node.exit)) {
      return stepId;
    }
  }

  return subgraph.root;
};

const buildRemappedGraph = ({ actionIds, sourceGraph, idMap }) => {
  const graph = {};
  for (const currentId of actionIds) {
    const newCurrentId = idMap[currentId];
    const originalNode = sourceGraph[currentId];

    graph[newCurrentId] = {
      exit: originalNode?.exit && idMap[originalNode.exit] ? idMap[originalNode.exit] : null,
      child: originalNode?.child && idMap[originalNode.child] ? idMap[originalNode.child] : null,
      branches: Array.isArray(originalNode?.branches) ? originalNode.branches.map((branchId) => (branchId && idMap[branchId] ? idMap[branchId] : null)) : []
    };
  }

  return graph;
};

const connectSubgraphsInSequence = ({ graph, subgraphs, sourceGraph, idMap }) => {
  for (let i = 0; i < subgraphs.length - 1; i++) {
    const currentSubgraph = subgraphs[i];
    const nextSubgraph = subgraphs[i + 1];
    const lastStepInSubgraph = getLastStepInSubgraph(currentSubgraph, sourceGraph);

    if (lastStepInSubgraph && graph[idMap[lastStepInSubgraph]]) {
      graph[idMap[lastStepInSubgraph]] = {
        ...graph[idMap[lastStepInSubgraph]],
        exit: idMap[nextSubgraph.root]
      };
    }
  }

  return graph;
};

const determineTargetRelation = (parentStepId, parentStep, targetSteps, copiedSteps, sourceGraph) => {
  // Find the parent step data to understand its capabilities
  const parentStepData = targetSteps.find((step) => step.id === parentStepId);

  if (!parentStepData) {
    return { relation: 'exit', index: null };
  }

  // Check if parent can have branches (like ifElse steps)
  const canHaveBranches = parentStepData.appSlug === 'ifElse';

  // Check if parent can have children (like iterate steps)
  const canHaveChildren = ['iterate'].includes(parentStepData.appSlug) || parentStepData.options?.pagination?.type === 'batch';

  // If we have copied steps, try to determine their original relationship
  if (copiedSteps && sourceGraph) {
    const firstCopiedStep = copiedSteps[0];
    if (firstCopiedStep) {
      const originalParent = getParentGraphStep(firstCopiedStep.id, sourceGraph);

      // If the original steps were in a branch relationship and the target can have branches
      if (originalParent?.relation === 'branch' && canHaveBranches) {
        // Find the first available branch slot
        const currentBranches = parentStep.branches || [];
        const firstEmptyBranchIndex = currentBranches.findIndex((branch) => !branch);
        if (firstEmptyBranchIndex !== -1) {
          return { relation: 'branch', index: firstEmptyBranchIndex };
        }
        // If no empty branch slots, add to the end
        return { relation: 'branch', index: currentBranches.length };
      }

      // If the original steps were children and the target can have children
      if (originalParent?.relation === 'child' && canHaveChildren && !parentStep.child) {
        return { relation: 'child', index: null };
      }
    }
  }

  // Default fallback logic based on parent capabilities
  if (canHaveBranches && (!parentStep.branches || parentStep.branches.filter((b) => b).length === 0)) {
    // If parent can have branches and has no branches yet, use first branch
    return { relation: 'branch', index: 0 };
  }

  if (canHaveChildren && !parentStep.child) {
    // If parent can have children and has no child yet, use child relationship
    return { relation: 'child', index: null };
  }

  // Default to exit relationship
  return { relation: 'exit', index: null };
};

export const duplicateWorkflowStepsByIds = ({ workflow, ids, userId, parentStepId, copiedSteps, copiedGraph }) => {
  const sourceSteps = copiedSteps || [...(workflow?.steps || [])];
  const sourceGraph = copiedGraph || (copiedSteps ? buildGraphFromSteps(copiedSteps) : cloneWorkflowGraph(workflow?.graph));
  const targetSteps = [...(workflow?.steps || [])];
  const targetGraph = cloneWorkflowGraph(workflow?.graph);
  const sourceStepById = new Map(sourceSteps.map((step) => [step.id, step]));
  const filteredIds = expandWorkflowFolderStepIds({ graph: sourceGraph, steps: sourceSteps, stepIds: ids });

  // Grab only the action ids
  const actionIds = filteredIds.filter((n) => {
    const step = sourceSteps.find((m) => m.id === n);
    return !['schedule'].includes(step?.appSlug) && step?.type !== 'trigger';
  });

  // Build a clone id map
  const idMap = {};
  filteredIds?.forEach((n) => {
    idMap[n] = randomId(12, 'all');
  });

  const newDate = new Date().toISOString();

  // Duplicate the steps
  const newSteps = [];
  for (let i = 0; i < filteredIds?.length; ++i) {
    const stepId = filteredIds[i];
    const step = { ...sourceStepById.get(stepId) };
    const newStepId = idMap[stepId];
    const newStep = {
      ...step,
      id: newStepId,
      // Tenant ownership of a duplicated step belongs to the TARGET workflow's company,
      // never the source row's. Without this, cross-workflow paste from a different
      // tenant inserts an orphan row invisible to selectWorkflow's company-scoped read.
      companyId: workflow?.companyId,
      workflowId: workflow?.id,
      createdOn: newDate,
      updatedOn: newDate,
      createdBy: userId,
      updatedBy: userId
    };
    newSteps.push(newStep);
  }

  const selectedSourceGraph = getWorkflowGraphSelection({ graph: sourceGraph, stepIds: actionIds });
  let newGraph = buildRemappedGraph({ actionIds, sourceGraph: selectedSourceGraph, idMap });
  const subgraphs = findDisconnectedSubgraphs(actionIds, selectedSourceGraph);

  if (subgraphs.length > 1) {
    newGraph = connectSubgraphsInSequence({ graph: newGraph, subgraphs, sourceGraph: selectedSourceGraph, idMap });
  }

  // Determine the connection strategy based on context
  if (copiedSteps || parentStepId) {
    // Handle parent connection if specified
    if (parentStepId) {
      const parentStep = targetGraph[parentStepId];
      if (!parentStep) {
        return null;
      }

      if (subgraphs.length === 0) {
        return null;
      }

      // Connect subgraphs in sequence: parent → subgraph1 → subgraph2 → ... → original_parent_exit
      const firstSubgraph = subgraphs[0];
      const firstNewStepId = idMap[firstSubgraph.root];

      // Get the last step of the final subgraph for parent connection
      const lastSubgraph = subgraphs[subgraphs.length - 1];
      const lastNewStepId = idMap[getLastStepInSubgraph(lastSubgraph, selectedSourceGraph)];

      // Determine the appropriate relationship type and index
      const { relation, index } = determineTargetRelation(parentStepId, parentStep, targetSteps, copiedSteps, selectedSourceGraph);

      // Handle different relationship types
      if (relation === 'exit') {
        const originalParentExit = parentStep.exit;

        // Connect parent to first new step via exit
        targetGraph[parentStepId] = {
          ...parentStep,
          exit: firstNewStepId
        };

        // Connect last new step to original parent's exit (preserving the original flow)
        // This inserts the copied steps between the parent and its original next step
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentExit
          };
        }
      } else if (relation === 'child') {
        const originalParentChild = parentStep.child;

        // Connect parent to first new step via child
        targetGraph[parentStepId] = {
          ...parentStep,
          child: firstNewStepId
        };

        // Connect last new step to original parent's child (preserving the original flow)
        // This inserts the copied steps between the parent and its original child
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentChild
          };
        }
      } else if (relation === 'branch' && index !== null) {
        const originalParentBranches = [...(parentStep.branches || [])];
        const originalBranchConnection = originalParentBranches[index];

        // Ensure branches array is large enough
        while (originalParentBranches.length <= index) {
          originalParentBranches.push(null);
        }

        // Connect parent to first new step via branch
        originalParentBranches[index] = firstNewStepId;
        targetGraph[parentStepId] = {
          ...parentStep,
          branches: originalParentBranches
        };

        // For branches, only connect to original branch connection if there was one
        // This allows replacing existing branch content or adding to empty branch slots
        if (newGraph[lastNewStepId] && originalBranchConnection) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalBranchConnection
          };
        }
      }
    }
  } else {
    if (subgraphs.length > 0) {
      const firstNewStepId = idMap[subgraphs[0].root];
      const lastSubgraph = subgraphs[subgraphs.length - 1];
      const lastOriginalStepId = getLastStepInSubgraph(lastSubgraph, selectedSourceGraph);
      const lastNewStepId = idMap[lastOriginalStepId];
      const originalLastExit = targetGraph[lastOriginalStepId]?.exit;

      if (targetGraph[lastOriginalStepId]) {
        targetGraph[lastOriginalStepId] = {
          ...targetGraph[lastOriginalStepId],
          exit: firstNewStepId
        };
      }

      if (newGraph[lastNewStepId]) {
        newGraph[lastNewStepId] = {
          ...newGraph[lastNewStepId],
          exit: originalLastExit || null
        };
      }
    }
  }

  // Merge graphs and steps
  const fullGraph = { ...targetGraph, ...newGraph };
  const fullSteps = [...targetSteps, ...newSteps];

  return { graph: fullGraph, steps: fullSteps, newSteps, timestamp: newDate };
};
